/**
 * Episode Status Lifecycle
 *
 * Clip-based workflow (NEW):
 *   draft -> generating_scripts -> scripts_pending_review -> scripts_approved
 *   -> generating_audio -> audio_pending_review -> audio_approved
 *   -> merging -> merged -> publishing -> published
 *
 * Legacy single-article workflow:
 *   generating_script -> script_ready -> generating_audio -> audio_ready -> publishing -> published
 *
 * Failure states (can retry from each):
 *   Any generating state -> failed
 *   publishing -> publishing_failed
 */
export enum EpisodeStatus {
  // ============================================
  // CLIP-BASED WORKFLOW STATUSES (NEW)
  // ============================================
  DRAFT = 'draft',                                      // Episode created, no processing started
  GENERATING_SCRIPTS = 'generating_scripts',            // Scripts being generated for clips
  SCRIPTS_PENDING_REVIEW = 'scripts_pending_review',    // All scripts ready, awaiting approval
  SCRIPTS_APPROVED = 'scripts_approved',                // Scripts approved (Gate 1 passed)
  AUDIO_PENDING_REVIEW = 'audio_pending_review',        // All audio ready, awaiting approval
  AUDIO_APPROVED = 'audio_approved',                    // Audio approved (Gate 2 passed)
  MERGING = 'merging',                                  // Merge in progress
  MERGED = 'merged',                                    // Final audio ready
  FAILED = 'failed',                                    // Something failed

  // ============================================
  // LEGACY SINGLE-ARTICLE WORKFLOW STATUSES
  // ============================================
  PENDING = 'pending',                      // Episode created, awaiting processing
  GENERATING_SCRIPT = 'generating_script',  // Script being generated by Make.com
  SCRIPT_READY = 'script_ready',            // Script ready for review
  SCRIPT_FAILED = 'script_failed',          // Script generation failed (can retry)
  GENERATING_AUDIO = 'generating_audio',    // Audio being generated by Make.com
  AUDIO_READY = 'audio_ready',              // Audio ready for review
  AUDIO_FAILED = 'audio_failed',            // Audio generation failed (can retry)

  // ============================================
  // SHARED STATUSES
  // ============================================
  PUBLISHING = 'publishing',                // Being published to PodBean
  PUBLISHED = 'published',                  // Successfully published
  PUBLISHING_FAILED = 'publishing_failed',  // Publishing failed (can retry)
  ARCHIVED = 'archived',                    // Archived/inactive
}

/**
 * Merge Preview data (included in episode response when status allows merging)
 * Contains all the building blocks needed for frontend to construct FTP stream URLs
 */
export interface MergePreview {
  companyId: string;
  introPath: string | null;
  introDuration: number | null;
  outroPath: string | null;
  outroDuration: number | null;
  commercial1Path: string | null;
  commercial1Duration: number | null;
  commercial2Path: string | null;
  commercial2Duration: number | null;
  commercial3Path: string | null;
  commercial3Duration: number | null;
  commercial4Path: string | null;
  commercial4Duration: number | null;
}

/**
 * Complete podcast episode document
 * Matches FIRESTORE-DATABASE-SCHEMA.md specification
 */
export interface PodcastEpisode {
  // ============================================
  // COMPANY ISOLATION (REQUIRED)
  // ============================================
  companyId: string;

  // ============================================
  // EPISODE IDENTIFICATION
  // ============================================
  id: string;
  status: EpisodeStatus;

  // ============================================
  // SOURCE ARTICLE DATA
  // ============================================
  articleId: string;
  articleTitle: string;
  articleUrl: string;
  articleContent: string;
  articlePublishDate: Date;
  articleAuthor: string;
  articleExcerpt: string;
  sourcePlatform: string;

  // ============================================
  // EPISODE METADATA (User-Editable)
  // ============================================
  title: string;             // Max 255 chars
  description: string;       // Max 1000 chars
  coverArtUrl: string | null;
  artworkThumbnail?: string; // Base64 encoded 100x100 thumbnail for list views

  // ============================================
  // SCRIPT GENERATION
  // ============================================
  scriptContent: string | null;      // Generated script from Make.com

  // ============================================
  // AUDIO GENERATION
  // ============================================
  audioUrl: string | null;
  introBumperId: string | null;
  outroBumperId: string | null;

  // ============================================
  // CLIP-BASED WORKFLOW TRACKING (NEW)
  // ============================================
  clipCount?: number;            // Total clips in episode
  scriptsReady?: number;         // Count of clips with script_ready or later
  scriptsApproved?: number;      // Count of clips with script_approved or later
  audiosReady?: number;          // Count of clips with audio_ready or later
  audiosApproved?: number;       // Count of clips with audio_approved
  failedClipCount?: number;      // Count of clips in failed state (script_failed or audio_failed)
  previousStatus?: EpisodeStatus; // Status before FAILED (for recovery when clips are fixed)

  // Bumper configuration (override company defaults)
  introBumperPath?: string | null;   // SFTP path
  outroBumperPath?: string | null;   // SFTP path

  // Final merged audio
  mergedAudioPath?: string | null;   // SFTP path to merged file

  // Merge preview data (populated by API when status is audio_approved, merged, or publishing_failed)
  // Contains company audio settings needed to build FTP stream URLs for preview
  mergePreview?: MergePreview;

  // ============================================
  // AI-GENERATED SUGGESTIONS
  // ============================================
  suggestedTitles?: string[];
  suggestedDescriptions?: string[];
  suggestionsStatus?: 'idle' | 'generating' | 'ready' | 'failed';
  suggestionsError?: string;

  // ============================================
  // LEGACY WORKFLOW TRACKING
  // ============================================
  workflowRequestId: string | null;
  scriptGeneratedAt: Date | null;
  processingStartedAt: Date | null;
  processingCompletedAt: Date | null;
  errorMessage: string | null;
  errorDetails: string | null;

  // ============================================
  // PUBLISHING
  // ============================================
  publishedAt: Date | null;
  publishingProvider: string | null;      // 'podbean'
  publishingProviderId: string | null;    // External episode ID
  publishingProviderUrl: string | null;   // Direct link to published episode
  mediaUrl: string | null;                // Direct audio URL from Podbean
  scheduledPublishDate: Date | null;      // When episode is scheduled to go live (null = immediate)

  // ============================================
  // METADATA (Standard Fields)
  // ============================================
  createdAt: Date;
  createdBy: string;
  createdByName?: string;       // Display name of creator (populated by API)
  createdByAvatarUrl?: string;  // Avatar URL of creator (populated by API)
  updatedAt: Date;
  updatedBy: string;
  updatedByName?: string;       // Display name of last editor (populated by API)
  updatedByAvatarUrl?: string;  // Avatar URL of last editor (populated by API)
}

/**
 * Input for creating a new draft episode from an article
 */
export interface CreateEpisodeInput {
  // Article source data
  articleId: string;
  articleTitle: string;
  articleUrl: string;
  articleContent: string;
  articlePublishDate: Date;
  articleAuthor: string;
  articleExcerpt: string;
  sourcePlatform: string;

  // Optional: Override default episode metadata
  title?: string;              // Defaults to articleTitle
  description?: string;        // Defaults to articleExcerpt
  coverArtUrl?: string | null;
}

/**
 * Input for updating episode metadata
 */
export interface UpdateEpisodeInput {
  title?: string;
  description?: string;
  coverArtUrl?: string | null;
  introBumperId?: string | null;
  outroBumperId?: string | null;
}

/**
 * Input for submitting episode for audio generation
 */
export interface SubmitEpisodeInput {
  episodeId: string;
  introBumperId?: string | null;
  outroBumperId?: string | null;
}

/**
 * Response from script generation workflow
 */
export interface ScriptGenerationCallbackPayload {
  episodeId: string;
  companyId: string;
  status: 'success' | 'failed';
  scriptContent?: string; // Generated script from Make.com
  errorMessage?: string;
  errorDetails?: string;
  workflowRequestId?: string;
}

/**
 * Response from audio generation workflow
 */
export interface AudioGenerationCallbackPayload {
  episodeId: string;
  companyId: string;
  status: 'success' | 'failed';
  audioUrl?: string;
  errorMessage?: string;
  errorDetails?: string;
  processingDuration?: number; // seconds
  workflowRequestId?: string;
}

/**
 * Response from publishing workflow
 */
export interface PublishingCallbackPayload {
  episodeId: string;
  companyId: string;
  status: 'success' | 'failed';
  publishingProvider?: string;
  publishingProviderId?: string;
  publishingProviderUrl?: string;
  errorMessage?: string;
  errorDetails?: string;
  workflowRequestId?: string;
}

/**
 * Query options for listing episodes
 */
export interface EpisodeQueryOptions {
  status?: EpisodeStatus | EpisodeStatus[];  // Single status or array for multi-status queries
  limit?: number;
  page?: number;
  search?: string;
  orderBy?: 'createdAt' | 'updatedAt';
  order?: 'asc' | 'desc';
  companyIds?: string[];  // Filter by multiple companies (for multi-company users)
}

/**
 * Pagination metadata returned by paginated API endpoints
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Paginated response containing episodes and pagination info
 */
export interface PaginatedEpisodesResponse {
  episodes: PodcastEpisode[];
  meta: PaginationMeta;
}

/**
 * Aggregated episode metrics for a single company
 * Used by multi-company dashboard to show condensed stats
 */
export interface CompanyEpisodeStats {
  companyId: string;
  companyName: string;
  counts: {
    needsReview: number;
    inProgress: number;
    readyToPublish: number;
    published: number;
    failed: number;
  };
}

/**
 * Podcast chapter for Podbean
 * Used to create navigation markers within an episode
 */
export interface PodcastChapter {
  title: string;      // Chapter title (from article title)
  startTime: number;  // Start time in seconds from episode beginning
}
